import { useResizeObserver } from '#pplx/frontend/common/hooks/useResizeObserver.js';
import { useMemo } from 'react';
/**
 * Measures an element and returns CSS custom properties for its width and height.
 *
 * Use this when layout needs to reference an element whose size can change after
 * render. The returned object is intended to be passed to `style` and exposes
 * `${namespace}-width` and `${namespace}-height`. Fallback values are used until
 * the element has been measured.
 *
 * @example
 * ```tsx
 * const headerRef = useRef<HTMLDivElement>(null);
 * const headerSizeCssVars = useElementSizeCssVars({
 *   ref: headerRef,
 *   namespace: '--space-tabs-header',
 *   fallback: { height: 54 },
 * });
 *
 * return <div ref={headerRef} style={headerSizeCssVars} />;
 * ```
 */
export function useElementSizeCssVars({ ref, namespace, fallback = {}, }) {
    const { width, height } = useResizeObserver({ ref });
    return useMemo(() => ({
        [`${namespace}-width`]: `${width ?? fallback.width ?? 0}px`,
        [`${namespace}-height`]: `${height ?? fallback.height ?? 0}px`,
    }), [fallback.height, fallback.width, height, namespace, width]);
}
//# sourceMappingURL=useElementSizeCssVars.js.map